import { fetchCategories } from '~/app/[locale]/home/store/requests';
import { Filters } from '~/atoms';
import { IconName } from '~/components/core/Icon';
import { parameterIDs } from '~/constants/store-parameter-ids';
import { TranslateType } from '~/types-and-enums/generalTypes';
import {
  CartItemType,
  CategoryType,
  CategoryValuesType,
  ParamValueType,
  PriceUnitEnum,
  ProductCategoryBEnum,
  ProductCategoryFEnum,
  ProductPriceType,
  ProductType,
  SupportedCategoryType,
  ValueMappingType,
  WeeklyPrice,
} from '~/types-and-enums/storeTypes';
import { ProductCategory } from './claimUtils';
import {
  getFirstWorkingDayOfWeek,
  getFormattedDeliveryPeriod,
  getLastWorkingDayOfWeek,
  getWeekNumber,
} from './dateUtils';
import { ColorModeEnum, findKeyByValue, formatCurrency, isEmpty, isNotEmpty } from './generalUtils';

export const valueMapping = {
  [ProductCategoryBEnum.Modules]: ProductCategoryFEnum.Modules,
  [ProductCategoryBEnum.ModulesLowCarbon]: ProductCategoryFEnum.ModulesLowCarbon,
  [ProductCategoryBEnum.ModulesCommercialInfrastructure]:
    ProductCategoryFEnum.ModulesCommercialInfrastructure,
  [ProductCategoryBEnum.Batteries]: ProductCategoryFEnum.Batteries,
  [ProductCategoryBEnum.BatteriesLargeScaleBESS]: ProductCategoryFEnum.BatteriesLargeScaleBESS,
  [ProductCategoryBEnum.Inverters]: ProductCategoryFEnum.Inverters,
  [ProductCategoryBEnum.InvertersCommercialInfrastructure]:
    ProductCategoryFEnum.InverterCommercialInfrastructure,
  [ProductCategoryBEnum.Optimizer]: ProductCategoryFEnum.Optimizer,
  [ProductCategoryBEnum.ElectricCables]: ProductCategoryFEnum.ElectricCables,
  [ProductCategoryBEnum.Wallboxes]: ProductCategoryFEnum.Wallboxes,
  [ProductCategoryBEnum.MountingSystems]: ProductCategoryFEnum.MountingSystems,
  [ProductCategoryBEnum.BuildingAutomationIoTDevices]:
    ProductCategoryFEnum.BuildingAutomationIoTDevices,
  [ProductCategoryBEnum.XPS]: ProductCategoryFEnum.XPS,
  [ProductCategoryBEnum.Others]: ProductCategoryFEnum.Others,
};

export const getOnlySupportedCategories = (allCategories: CategoryType[]) => {
  const supportedCategoryNames = Object.keys(valueMapping);

  const supportedCategories = allCategories
    .map((category) => {
      if (supportedCategoryNames.includes(category.name))
        return {
          value: valueMapping[category.name as keyof typeof valueMapping],
          id: category.id,
        };
    })
    .filter((item) => item !== undefined);
  return supportedCategories;
};

export const getSanitizedCategoryName = (name?: string | null): string | null => {
  if (!name) return null;
  const typedValueMapping: ValueMappingType = valueMapping;
  return typedValueMapping[name] ?? null;
};

/**
 * Sets parameters based on the searchParams object.
 *
 * @param {Object} searchParams - An object containing key-value pairs for parameters.
 * @return {string} The concatenated string of parameters for the URL.
 */
export const setParameters = (searchParams: { [key: string]: string | undefined }) => {
  if (!searchParams || Object.keys(searchParams).length === 0) return '';

  let str = '';

  for (const [key, value] of Object.entries(searchParams)) {
    if (!value) continue; // Skip undefined or empty values

    if (key === 'query') {
      // Do nothing for `query` as per the original code
      continue;
    } else if (key === 'sort') {
      str += `&${key}=${value}`;
    } else if (key === 'category') {
      const allValues = value.split(',');
      let categoryNames = '';
      allValues.forEach((val) => {
        const categoryKey = findKeyByValue(valueMapping, val);
        if (categoryKey) {
          categoryNames += categoryNames.length > 0 ? `;${categoryKey}` : categoryKey;
        }
      });

      str += `&${encodeURIComponent('category.name=')}${encodeURIComponent(categoryNames)}`;
    } else if (key === 'page') {
      str += `&${key}=${+value - 1}`;
    } else {
      // Use "key=value" format for all other parameters
      str += `&${key}=${value}`;
    }
  }
  if (str.includes('&Brand')) {
    str = str.replace(/&Brand=([^&]*)/, (_, brands) => {
      // Replace commas with semicolons in the existing Brand parameter
      return `&parameter=Brand:${brands.replace(/,/g, ';')}`;
    });
  }
  return str;
};

export const findSelectedCategory = async (categoryName: string) => {
  const categories = await fetchCategories();
  const selectedCategory = categories?.find(
    (category: SupportedCategoryType) => category.value === categoryName,
  );

  return selectedCategory || null;
};

export const returnPriceString = (price: number, quantity?: number) => {
  const calculatedPrice = quantity ? price * quantity : price;
  return formatCurrency(calculatedPrice);
};

export const isOnlySearch = (allParams: { [key: string]: string | undefined }) => {
  const array = Object.keys(allParams);
  return array.length === 1 && array[0] === 'query';
};

export const getByParameter = ({
  array,
  param,
}: {
  array?: ParamValueType[];
  param: string;
}): string => {
  if (!array || !Array.isArray(array) || array.length === 0 || !param) {
    return '';
  }
  const env = process.env.NEXT_PUBLIC_ENV?.toLocaleLowerCase() ?? 'dev';
  // @ts-ignore
  const paramIds = parameterIDs?.[param as any]?.[env];

  const paramObject = array.find((item) => paramIds?.includes(item.parameterId));

  if (!paramObject || !paramObject?.value) {
    return '';
  }
  return paramObject ? paramObject.value : '';
};

export const mapProductsForResultList = (productsArray: ProductType[]) => {
  return isNotEmpty(productsArray)
    ? productsArray.map((product) => {
        return {
          id: product.id,
          name: product.name,
          brand: product.brand,
          category: {
            id: product.category.id,
            name: product.category.name as ProductCategory,
          },
          productImage: {
            value: product.product_image?.value,
          },
          price: product.price.base_price,
          feedback: product.price.feedback,
          earliest_delivery_date: product.price?.aggregation?.earliest_delivery_date,
          measurements: product.measurements,
        };
      })
    : [];
};

export const isAnyFilterSet = (filters: Filters) => {
  return Object.entries(filters).some(([key, value]) => {
    if (key === 'priceRange') {
      return (
        Array.isArray(value) && value.length === 2 && value.every((num) => typeof num === 'number')
      );
    }
    return value !== undefined;
  });
};

export const moveLongestValueToEnd = (array?: ParamValueType[]) => {
  if (isEmpty(array) || !array) return null;

  const longestValueObject = array.reduce((acc, curr) => {
    const accLength = acc?.value?.length ?? 0;
    const currLength = curr?.value?.length ?? 0;

    if (currLength > accLength) {
      return curr;
    }
    return acc;
  }, array[0]);

  // 45 is ~ two lines
  if ((longestValueObject?.value?.length ?? 0) <= 45) {
    return array;
  }

  const indexToRemove = array.indexOf(longestValueObject);
  if (indexToRemove !== -1) {
    array.splice(indexToRemove, 1);
  }

  array.push(longestValueObject);
  return array;
};

/**
 * Returns icon, name and link for each category.
 */
export const categoryValues: Record<ProductCategoryFEnum, CategoryValuesType> = {
  [ProductCategoryFEnum.Modules]: {
    icon: IconName.Modules,
    strokeWidth: 1.5,
    name: 'modules',
    link: ProductCategoryFEnum.Modules + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.ModulesLowCarbon]: {
    icon: IconName.Modules,
    strokeWidth: 1.5,
    name: 'modules-low-carbon',
    link: ProductCategoryFEnum.ModulesLowCarbon + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.ModulesCommercialInfrastructure]: {
    icon: IconName.Modules,
    strokeWidth: 1.5,
    name: 'modules-commercial-infrastructure',
    link: ProductCategoryFEnum.ModulesCommercialInfrastructure + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.Batteries]: {
    icon: IconName.Batteries,
    strokeWidth: 1.5,
    name: 'batteries',
    link: ProductCategoryFEnum.Batteries + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.BatteriesLargeScaleBESS]: {
    icon: IconName.Batteries,
    strokeWidth: 1.5,
    name: 'batteries-large-scale-bess',
    link: ProductCategoryFEnum.BatteriesLargeScaleBESS + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.Inverters]: {
    icon: IconName.Inverters,
    strokeWidth: 1.5,
    name: 'inverters',
    link: ProductCategoryFEnum.Inverters + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.InverterCommercialInfrastructure]: {
    icon: IconName.Inverters,
    strokeWidth: 1.5,
    name: 'inverter-commercial-infrastructure',
    link: ProductCategoryFEnum.InverterCommercialInfrastructure + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.Optimizer]: {
    icon: IconName.Optimizer,
    colorMode: 'fill' as ColorModeEnum,
    strokeWidth: 3,
    name: 'optimizer',
    link: ProductCategoryFEnum.Optimizer + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.ElectricCables]: {
    icon: IconName.Cables,
    strokeWidth: 1.5,
    name: 'electric-cables',
    link: ProductCategoryFEnum.ElectricCables + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.Wallboxes]: {
    icon: IconName.Wallboxes,
    colorMode: 'fill' as ColorModeEnum,
    strokeWidth: 3,
    name: 'wallboxes',
    link: ProductCategoryFEnum.Wallboxes + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.MountingSystems]: {
    icon: IconName.MountingSystems,
    colorMode: 'fill' as ColorModeEnum,
    strokeWidth: 3,
    name: 'mounting-systems',
    link: ProductCategoryFEnum.MountingSystems + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.BuildingAutomationIoTDevices]: {
    icon: IconName.Settings,
    strokeWidth: 1.5,
    name: 'building-automation-iot-devices',
    link: ProductCategoryFEnum.BuildingAutomationIoTDevices + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.XPS]: {
    icon: IconName.Insulation,
    colorMode: 'fill' as ColorModeEnum,
    strokeWidth: 4,
    name: 'xps',
    link: ProductCategoryFEnum.XPS + '?sort=popularity,desc',
  },
  [ProductCategoryFEnum.Others]: {
    icon: IconName.Others,
    colorMode: 'fill' as ColorModeEnum,
    strokeWidth: 1.25,
    name: 'others',
    link: ProductCategoryFEnum.Others + '?sort=popularity,desc',
  },
};

/**
 * Category icons for backend categories that are NOT part of the store's
 * `ProductCategoryFEnum` catalogue (e.g. financial products, services) but
 * can still appear as line items on an offer. Keys are lowercased for
 * case-insensitive lookup.
 */
const SPECIAL_CATEGORY_ICONS: Record<string, CategoryValuesType> = {
  'core financing': {
    icon: IconName.Billing,
    colorMode: 'stroke' as ColorModeEnum,
    strokeWidth: 1.5,
    name: 'core-financing',
    link: '',
  },
  // TT-3956: branded discount tag icon for "Discount" line items. Same stroke/weight treatment as the
  // €-receipt (Core Financing) icon it sits beside. Previously fell through to the generic "Others" icon.
  discount: {
    icon: IconName.Discount,
    colorMode: 'stroke' as ColorModeEnum,
    strokeWidth: 1.5,
    name: 'discount',
    link: '',
  },
};

/**
 * TECH DEBT — name-keyed icon fallback. Some non-physical line items arrive with NO usable category:
 * "Logistics" items come through with category "Other" (that is what the badge shows) and only their
 * product NAME = "Logistics". There is no product_subcategory / product_category to key on, so — as an
 * explicit, greppable, REMOVABLE fallback — we map by product NAME here.
 *
 * This is NOT a pattern to extend: the correct fix is for the backend / Salesforce to give these items a
 * real category, at which point add it to SPECIAL_CATEGORY_ICONS above and delete the matching entry here.
 * Keys are lowercased for case-insensitive lookup.
 */
const SPECIAL_NAME_ICONS: Record<string, CategoryValuesType> = {
  logistics: {
    icon: IconName.DeliveryInProgress,
    colorMode: 'stroke' as ColorModeEnum,
    strokeWidth: 1.5,
    name: 'logistics',
    link: '',
  },
};

/**
 * Category (product_subcategory || product_category) key for the non-physical "Core Financing" line
 * item. Kept next to SPECIAL_CATEGORY_ICONS so the single discriminator string lives in one place.
 */
export const CORE_FINANCING_CATEGORY = 'core financing';

/**
 * True when a line item's category is the special "Core Financing" product. Case-insensitive, matching
 * the SPECIAL_CATEGORY_ICONS lookup. Used to render the CORE Financing brand lockup instead of an icon.
 */
export const isCoreFinancingCategory = (categoryName?: string | null): boolean =>
  (categoryName ?? '').toLowerCase().trim() === CORE_FINANCING_CATEGORY;

/**
 * Canonical product NAME of the non-physical "Core Financing" line item.
 *
 * BACKEND GAP — the offers LIST endpoint (quote list) does NOT return the authoritative category
 * (product_subcategory); it only returns product_category, which for this item is not "Core Financing".
 * The detail endpoint (/quotes/{id}/offer-details) does return it, which is why category-based detection
 * only works there. On the list surface the item can therefore only be recognised by its
 * backend-controlled, non-localised name. (Discount is NOT name-matchable — its name is a free-form,
 * localised label such as "Loyalitätsrabatt" — so it cannot be branded on the list until the API exposes
 * the category there. Do NOT add discount names here.)
 */
export const CORE_FINANCING_NAME = 'core financing';

/**
 * True when a line item is the "Core Financing" product identified by its NAME (case-insensitive).
 * Companion to isCoreFinancingCategory for surfaces where the category enum is unavailable (offers list).
 */
export const isCoreFinancingName = (productName?: string | null): boolean =>
  (productName ?? '').toLowerCase().trim() === CORE_FINANCING_NAME;

/**
 * Returns the category-icon metadata (icon, stroke width, color mode) for a
 * given category name. The name can be either a backend value
 * (e.g. "PV Modules"), a frontend value (e.g. "modules"), a special
 * non-product category ("Core Financing"), or any display / translated
 * string — a best-effort match is performed.
 *
 * When the category is entirely missing (undefined / empty), `null` is
 * returned so the caller can render its own generic fallback. When a
 * category string IS provided but doesn't match any known category, we
 * fall back to the generic "PV Others" icon so the UI still shows a
 * meaningful category-style badge rather than a plain placeholder.
 *
 * Use this to show a category-specific fallback icon (similar to the
 * store category tiles) whenever a product image is missing.
 */
export const getCategoryIconMeta = (categoryName?: string | null): CategoryValuesType | null => {
  if (!categoryName) return null;
  const typedValueMapping: ValueMappingType = valueMapping;
  const normalized = categoryName.toLowerCase().trim();

  // 1. Special non-product categories (e.g. "Core Financing" -> billing icon)
  if (SPECIAL_CATEGORY_ICONS[normalized]) {
    return SPECIAL_CATEGORY_ICONS[normalized];
  }

  // 2. Backend enum value, e.g. "PV Modules" -> ProductCategoryFEnum.Modules
  const feFromBackend = typedValueMapping[categoryName];
  if (feFromBackend && categoryValues[feFromBackend as ProductCategoryFEnum]) {
    return categoryValues[feFromBackend as ProductCategoryFEnum];
  }

  // 3. Frontend enum value, e.g. "modules", used directly
  if (categoryValues[categoryName as ProductCategoryFEnum]) {
    return categoryValues[categoryName as ProductCategoryFEnum];
  }

  // 4. Best-effort loose match against backend / frontend names (case-insensitive)
  const backendEntries = Object.entries(typedValueMapping) as Array<[string, ProductCategoryFEnum]>;
  const backendMatch = backendEntries.find(([beName]) => beName.toLowerCase() === normalized);
  if (backendMatch) return categoryValues[backendMatch[1]] ?? null;

  const feEntries = Object.values(ProductCategoryFEnum);
  const feMatch = feEntries.find((feName) => feName.toLowerCase() === normalized);
  if (feMatch) return categoryValues[feMatch] ?? null;

  // 5. Unknown category string — fall back to the "PV Others" icon so the UI
  //    still renders a meaningful category badge instead of the plain placeholder.
  return categoryValues[ProductCategoryFEnum.Others] ?? null;
};

/**
 * Resolve a line item's placeholder-icon metadata from its category, with a NAME-based fallback for
 * items that carry no meaningful category (see SPECIAL_NAME_ICONS — e.g. "Logistics" arrives as
 * category "Other" + name "Logistics"). Rules:
 *   1. A SPECIFIC category always wins (Core Financing, Discount, PV Modules, …).
 *   2. Otherwise — when the category is missing or only resolves to the generic "others" icon — the
 *      product NAME is consulted against SPECIAL_NAME_ICONS.
 *   3. Otherwise the category result (generic "others" or null) is returned unchanged.
 *
 * Delegates category resolution to getCategoryIconMeta so the mapping is not duplicated.
 */
export const getLineItemIconMeta = (
  categoryName?: string | null,
  productName?: string | null,
): CategoryValuesType | null => {
  const byCategory = getCategoryIconMeta(categoryName);

  // getCategoryIconMeta returns the generic "others" meta for a present-but-unknown category; treat
  // that (and a null result) as "no specific match" so a name-based special can take over.
  if (byCategory && byCategory.name !== categoryValues[ProductCategoryFEnum.Others].name) {
    return byCategory;
  }

  const nameKey = (productName ?? '').toLowerCase().trim();
  if (SPECIAL_NAME_ICONS[nameKey]) return SPECIAL_NAME_ICONS[nameKey];

  return byCategory;
};

export const iconList = [
  { icon: 'modules', name: ProductCategoryBEnum.Modules },
  { icon: 'modules', name: ProductCategoryBEnum.ModulesLowCarbon },
  {
    icon: 'modules',
    name: ProductCategoryBEnum.ModulesCommercialInfrastructure,
  },
  { icon: 'batteries', name: ProductCategoryBEnum.Batteries },
  { icon: 'batteries', name: ProductCategoryBEnum.BatteriesLargeScaleBESS },
  { icon: 'inverters', name: ProductCategoryBEnum.Inverters },
  {
    icon: 'inverters',
    name: ProductCategoryBEnum.InvertersCommercialInfrastructure,
  },
  { icon: 'optimizer', name: ProductCategoryBEnum.Optimizer },
  { icon: 'cables', name: ProductCategoryBEnum.ElectricCables },
  { icon: 'wallboxes', name: ProductCategoryBEnum.Wallboxes },
  { icon: 'mounting-systems', name: ProductCategoryBEnum.MountingSystems },
  { icon: 'building-automation', name: ProductCategoryBEnum.BuildingAutomationIoTDevices },
  { icon: 'insulation', name: ProductCategoryBEnum.XPS },
  { icon: 'others', name: ProductCategoryBEnum.Others },
] as const;

export { ProductCategoryFEnum };

/**
 * Returns the unit object for a given parameter.
 * @param informationArray - measurements of the products (array of objects)
 * @param parameter - parameter to get the unit for
 */
export const getUnitsByParameter = (
  informationArray: ProductType['measurements'] | ProductType['main_parameters'],
  parameter: string,
) => {
  return informationArray?.find((info) => info.parameter === parameter);
};

/**
 * Returns the message to show to the customer based on the error.
 * @param isShippingAddressInvalid - is the shipping address invalid
 * @param isPriceExpired - is the price expired
 * @param isProductOutOfStock - is the product out of stock
 * @param isSelectedQuantityNotAvailable - is the selected quantity not available
 * @param isSelectedWeekNotAvailable - is the selected week not available
 * @returns
 */
export const getErrorMessageForCustomer = ({
  isShippingAddressInvalid,
  isPriceExpired,
  isProductOutOfStock,
  isSelectedQuantityNotAvailable,
  isSelectedWeekNotAvailable,
  noAvailablePrice,
  isPriceZero,
  isOverAvailableQuantity,
}: {
  isShippingAddressInvalid: boolean;
  isPriceExpired: boolean;
  isProductOutOfStock: boolean;
  isSelectedQuantityNotAvailable: boolean;
  isSelectedWeekNotAvailable: boolean;
  noAvailablePrice?: boolean;
  isPriceZero?: boolean;
  isOverAvailableQuantity?: boolean;
}) => {
  // This order of showing the error messages is important
  switch (true) {
    case isOverAvailableQuantity:
      return 'general.theres-only-maximum-available-quantity';
    case isShippingAddressInvalid:
      return 'page.shop.product-details.enter-valid-delivery-address';
    case noAvailablePrice || isPriceZero:
      return 'page.store.the-product-currently-has-no-available-price';
    case isPriceExpired:
      return 'page.shop.product-details.the-price-has-expired';
    case isProductOutOfStock:
      return 'page.shop.product-details.product-is-out-of-stock';
    case isSelectedQuantityNotAvailable:
      return 'page.shop.product-details.there-are-only-max-pieces-available';
    case isSelectedWeekNotAvailable:
      return 'page.shop.product-details.product-is-only-available-from-week';
    default:
      return '';
  }
};

/**
 * Returns true if the price is not available.
 * - If all logistics cost values are zero
 * - If feedback.ERR_NO_MARGIN_FOUND is not null or undefined
 * - If selected_pq_price_expiration_date is undefined, null, or an invalid date
 * @param priceEngineData
 * @returns boolean
 */
export const isPriceNotAvailable = (priceEngineData: ProductPriceType) => {
  if (!priceEngineData) return false;
  // true -> if all logistics cost values are zero
  const logistics_cost = priceEngineData?.logistics_cost;
  const allZeroLogistics = logistics_cost
    ? Object.values(logistics_cost).every((value) => value === 0)
    : false;

  // true -> if ERR_NO_MARGIN_FOUND has "" or something meaningful, other than null or undefined
  const noMarginAvailable = priceEngineData?.feedback?.ERR_NO_MARGIN_FOUND != null;

  // true -> if selected_pq_price_expiration_date is undefined, null, or an invalid date
  const expirationDate = priceEngineData?.aggregation?.selected_pq_price_expiration_date;
  const isExpirationDateInvalid =
    !expirationDate || new Date(expirationDate).toString() === 'Invalid Date';

  // Return true if any of the conditions are met
  return allZeroLogistics || noMarginAvailable || isExpirationDateInvalid;
};

const parseLocalDate = (dateString: string) => {
  // "2025-08-01" => new Date(2025, 7, 1) (months are 0-based)
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

/**
 * Returns Week number and estimated arrival week.
 */
export const getWeekNumberAndDateRange = ({
  translate,
  date,
}: {
  translate?: TranslateType;
  date: string;
}): { weekNumber: string; dateRange: string } => {
  const isoDate = parseLocalDate(date);
  const firstWorkingDateOfWeek = getFirstWorkingDayOfWeek(isoDate);
  const lastWorkingDateOfWeek = getLastWorkingDayOfWeek(isoDate);
  const weekNumber = `${translate ? translate('general.calendar-week') : ''}${getWeekNumber(
    isoDate,
  )}`;

  const dateRange = getFormattedDeliveryPeriod(firstWorkingDateOfWeek, lastWorkingDateOfWeek);

  return {
    weekNumber,
    dateRange,
  };
};

/**
 * Returns an array of dates for the next 16 weeks starting from the given start date.
 * @param startDate (Date | string) - The start date from which to generate the weekly dates (can be Date object or ISO string)
 * @param numberOfWeeks (number) - Number of weeks to generate dates for (default: 16)
 */
export const generateWeeklyDates = ({
  startDate,
  numberOfWeeks = 16,
}: {
  startDate: Date | string;
  numberOfWeeks: number;
}) => {
  const dates = [];
  const startDateObj = typeof startDate === 'string' ? new Date(startDate) : startDate;

  for (let i = 0; i < numberOfWeeks; i++) {
    const newDate = new Date(startDateObj);
    newDate.setDate(startDateObj.getDate() + i * 7);
    dates.push(newDate.toISOString());
  }

  return dates;
};

/**
 * Returns the translated text for the selected unit based on the quantity.
 * @param selectedUnit - The unit selected by the user.
 * @param selectedQuantity - The quantity selected by the user.
 * @param translate - The translation function.
 * @returns Translated text for the selected unit.
 */
export const getSelectedUnitTranslationText = ({
  selectedUnit,
  selectedQuantity,
  translate,
}: {
  selectedUnit: PriceUnitEnum;
  selectedQuantity: number;
  translate: TranslateType;
}) => {
  switch (selectedUnit) {
    case PriceUnitEnum.Container:
      return translate(selectedQuantity === 1 ? 'page.unit.container' : 'page.unit.containers');
    case PriceUnitEnum.Pallet:
      return translate(selectedQuantity === 1 ? 'page.unit.pallet' : 'page.unit.pallets');
    default:
      return translate(selectedQuantity === 1 ? 'page.unit.unit' : 'page.unit.units');
  }
};

/**
 * Retrieves the appropriate icon name based on the selected price unit.
 *
 * @param selectedUnit - The selected price unit of type `PriceUnitEnum`.
 * @returns The corresponding icon name from the `IconName` enumeration.
 *
 * @remarks
 * - Returns `IconName.Container` if the selected unit is `PriceUnitEnum.Container`.
 * - Returns `IconName.Pallets` if the selected unit is `PriceUnitEnum.Pallet`.
 * - Returns `IconName.Pieces` for any other unit (default case).
 */
export const getIconBasedOnUnit = (selectedUnit: PriceUnitEnum): IconName => {
  switch (selectedUnit) {
    case PriceUnitEnum.Container:
      return IconName.Container;
    case PriceUnitEnum.Pallet:
      return IconName.Pallets;
    default:
      return IconName.Pieces;
  }
};

/**
 * Generates weekly dates and extracts product measurements
 * @param product - The product object containing price and measurements
 * @returns Object containing weekly dates and measurement values
 */
export const getProductDateAndMeasurements = (product: ProductType) => {
  const weeklyDates = generateWeeklyDates({
    startDate: product?.price?.aggregation?.earliest_delivery_date || new Date(),
    numberOfWeeks: 16,
  });

  const { measurements } = product;
  const unitsPerContainerNumericValue = getUnitsByParameter(
    measurements,
    'Units per container',
  )?.numeric_value;
  const unitsPerContainer = Number(unitsPerContainerNumericValue || 0);
  const unitPerPalletNumericValue = getUnitsByParameter(
    measurements,
    'Units per pallet',
  )?.numeric_value;
  const unitsPerPallet = Number(unitPerPalletNumericValue || 1);
  const soldInMultiplesOf = getUnitsByParameter(
    measurements,
    'Can only be sold in multiples of',
  )?.numeric_value;

  return {
    weeklyDates,
    unitsPerContainer,
    unitsPerPallet,
    soldInMultiplesOf,
  };
};

/**
 * Returns true if the pieces selector should be hidden.
 * - If soldInMultiplesOf is not set, return false.
 * - If soldInMultiplesOf is set and is greater than 1 and is equal to unitsPerPallet, return true.
 * - Otherwise, return false.
 * @param unitsPerPallet - The number of units per pallet.
 * @param soldInMultiplesOf - The quantity in which items are sold.
 */
export const hidePiecesSelector = ({
  unitsPerPallet,
  soldInMultiplesOf,
}: {
  unitsPerPallet: number;
  soldInMultiplesOf?: number;
}) => {
  return !soldInMultiplesOf ? false : soldInMultiplesOf > 1 && soldInMultiplesOf === unitsPerPallet;
};

/**
 * Determines if the unit should be hidden based on quantity constraints.
 * @param soldInMultiplesOf - The quantity in which items are sold
 * @param units - The total available units
 * @returns true if the unit should be hidden, otherwise false
 */
export const checkIfUnitCanBeHidden = ({
  soldInMultiplesOf,
  units,
}: {
  soldInMultiplesOf?: number;
  units: number;
}): boolean =>
  !soldInMultiplesOf ? false : soldInMultiplesOf > units || units % soldInMultiplesOf !== 0;

/**
 * Retrieves price data from available price quotes.
 * @param available_pqs - Array of available price quotes
 * @param cart_items - Array of cart items
 * @returns Object containing available quantity, minimum price, maximum price, and earliest valid date
 */
export const getPriceData = (priceData: WeeklyPrice, cart_items: CartItemType[]) => {
  const { available_pqs, start_date } = priceData;

  // items with same pq_id in cart and sum of it
  const itemsInCart = cart_items.reduce((sum, item) => {
    if (available_pqs.some((pq) => pq.pq_id === item.pq_id)) {
      return sum + (item.quantity || 0);
    }
    return sum;
  }, 0);

  // If any pq has available_quantity === -1, treat as infinite
  const hasInfinite = available_pqs.some((pq) => pq.available_quantity === -1);
  const availableQuantityFromPQs = available_pqs.reduce(
    (sum, pq) => sum + pq.available_quantity,
    0,
  );

  // Current week items that are available in the cart and sum of it
  const currentItemsInCart = cart_items.reduce((sum, item) => {
    if (available_pqs.some((pq) => pq.pq_id === item.pq_id && start_date === item.delivery_date)) {
      return sum + (item.quantity || 0);
    }
    return sum;
  }, 0);

  // 1. availableQuantity
  // If there is available_pqs, then availableQuantity is availableQuantityFromPQs - itemsInCart
  // If there is available_pqs and itemsInCart is more than availableQuantityFromPQs, then availableQuantity is 0
  // If there is available_pqs and itemsInCart is less than availableQuantityFromPQs, then availableQuantity is availableQuantityFromPQs - itemsInCart
  const availableQuantity = hasInfinite
    ? -1
    : availableQuantityFromPQs
    ? availableQuantityFromPQs - itemsInCart < 0
      ? 0
      : availableQuantityFromPQs - itemsInCart
    : 0;

  const isFullyAddedToCart = currentItemsInCart > 0 && availableQuantity === 0;

  // 2. from (min price)
  const from = available_pqs.length ? Math.min(...available_pqs.map((pq) => pq.sales_price)) : 0;

  // 3. to (max price)
  const to = available_pqs.length ? Math.max(...available_pqs.map((pq) => pq.sales_price)) : 0;

  // 4. validUntil (earliest date)
  const validUntil = available_pqs.length
    ? available_pqs.map((pq) => pq.valid_until).sort()[0]
    : undefined;

  return {
    currentItemsInCart,
    availableQuantity,
    from,
    to,
    validUntil,
    isFullyAddedToCart,
  };
};

/**
 * Checks if there is an error based on the totalSelectedPieces and price details
 * @param price - price details of the product
 * @param totalSelectedPieces - total selected pieces
 * @returns - object with error details
 */
export const checkIfThereIsAnError = ({
  price,
  totalSelectedPieces,
}: {
  price: ProductType['price'];
  totalSelectedPieces: number;
}) => {
  const {
    feedback: { ERR_NOT_ENOUGH_AVAILABLE_QUANTITY, ERR_NO_PQ_FOUND },
  } = price || { feedback: {} };

  const noPqFound = ERR_NO_PQ_FOUND != null;
  // If there is no PQ found, then show ProductOutOfStock error
  const isProductOutOfStock = noPqFound;

  // If available quantity is not -1 and total selected pieces is more than available quantity, then show QuantityNotAvailable error
  const isSelectedQuantityMoreThanTotalQuantity =
    price?.aggregation?.available_quantity !== -1 &&
    totalSelectedPieces > price?.aggregation?.available_quantity;

  // Checking if there is a ERR_NOT_ENOUGH_AVAILABLE_QUANTITY error
  const isQuantityOrDateError = ERR_NOT_ENOUGH_AVAILABLE_QUANTITY != null; // null and undefined, returns false, otherwise true

  //  Set quantity error if isQuantityOrDateError is true and total selected pieces is more than total quantity
  const isSelectedQuantityNotAvailable =
    isQuantityOrDateError && isSelectedQuantityMoreThanTotalQuantity;

  // Set week error if isQuantityOrDateError is true and total selected pieces is less than total quantity
  const isSelectedWeekNotAvailable =
    isQuantityOrDateError && !isSelectedQuantityMoreThanTotalQuantity;

  return {
    isSelectedQuantityNotAvailable,
    isSelectedWeekNotAvailable,
    isProductOutOfStock,
    noPqFound,
  };
};
